import { getDatabase, ref, get, update, push, query, orderByChild, equalTo } from 'firebase/database';
import {
    ParkingQueueEntry,
    ParkingQueueEntryData,
    ParkingQueueEntryInput,
    QueueCloseReason,
    QueueEntryStatus,
    QueueLeaseLinkInput,
    QueueLogEvent,
    QueueLogEventData,
    QueueLogEventType,
    QueueSpotTarget,
    QueueWish,
    QUEUE_CLOSE_REASON_LABELS,
} from '../types/parkingQueue';
import { ParkingSpotType } from '../types/Enums';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { DATE_ONLY_RE, parkingSpotLeaseLabel, todayStockholm } from '../Helpers/leaseUtils';
import { entriesAhead, rowFacts, SpotRef } from '../Helpers/parkingQueueUtils';
import { requireAdmin } from './adminGuard';
import { leasesApi } from './leasesApi';
import { fetchParkingQueueConfig } from './parkingQueueConfigApi';
import { recordActivity } from './activityApi';

const SPOT_TYPES = Object.values(ParkingSpotType) as string[];
const CLOSE_REASONS = Object.keys(QUEUE_CLOSE_REASON_LABELS);
/** Events of other types (the removed pre-release offer events) are skipped when parsed. */
const EVENT_TYPES: QueueLogEventType[] = [
    'created', 'queueDateChanged', 'internalChanged', 'areasChanged', 'spotTypesChanged', 'chargerChanged',
    'outletChanged', 'commentChanged', 'closed', 'reopened', 'deviation', 'leaseLinked', 'note',
];

const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) result[key] = entry;
    }
    return result as T;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const optionalWish = (value: unknown): QueueWish | undefined =>
    value === 'yes' || value === 'no' ? value : undefined;

const byKey = <T extends { key: string }>(a: T, b: T): number => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);

export type SpotTarget = QueueSpotTarget;

/**
 * Parkeringskö at `parkingQueue/{entryKey}` (flat, indexed on tenantKey,
 * admin read and write — spec docs/specs/parking-queue.md). Every mutation
 * is one root `update()` that carries its log event(s) and the entry's
 * `updatedBy/updatedWhen`, so the log can never lag the data. `create` embeds
 * the first event in the node value because the SDK rejects an update whose
 * paths are ancestor and descendant. The deviation check (FR20) runs here, in
 * `linkLease`, so it holds for every caller. Entries written before offers
 * were removed may still hold an `offers` child; it is not read.
 */
export class ParkingQueueApi {
    private db = getDatabase();

    getEntryPath(entryKey: string): string {
        return `parkingQueue/${entryKey}`;
    }

    private stamp(uid: string): { updatedBy: string; updatedWhen: string } {
        return { updatedBy: uid, updatedWhen: formatDateForStorage(new Date()) };
    }

    private newKey(path: string, what: string): string {
        const key = push(ref(this.db, path)).key;
        if (!key) throw new Error(`Kunde inte skapa nyckel för ${what}`);
        return key;
    }

    private event(uid: string, type: QueueLogEventType, extra: Partial<QueueLogEventData> = {}): QueueLogEventData {
        return stripUndefined({ type, actor: uid, when: formatDateForStorage(new Date()), ...extra });
    }

    /** `{ parkingQueue/{e}/log/{new}: event }` */
    private eventUpdate(entryKey: string, event: QueueLogEventData): Record<string, unknown> {
        return { [`${this.getEntryPath(entryKey)}/log/${this.newKey(`${this.getEntryPath(entryKey)}/log`, 'logghändelsen')}`]: event };
    }

    // MARK: - Parsing

    private parseEvent(raw: unknown, key: string): QueueLogEvent | null {
        if (!raw || typeof raw !== 'object') return null;
        const e = raw as Record<string, unknown>;
        if (typeof e.type !== 'string' || !EVENT_TYPES.includes(e.type as QueueLogEventType) || typeof e.actor !== 'string' || typeof e.when !== 'string') {
            return null;
        }
        return stripUndefined({
            key,
            type: e.type as QueueLogEventType,
            actor: e.actor,
            when: e.when,
            note: optionalString(e.note),
            from: optionalString(e.from),
            to: optionalString(e.to),
            aheadEntryKeys: e.aheadEntryKeys && typeof e.aheadEntryKeys === 'object'
                ? Object.fromEntries(Object.keys(e.aheadEntryKeys).map(k => [k, true as const]))
                : undefined,
        });
    }

    parseEntry(data: unknown, key: string): ParkingQueueEntry | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        const wantedAreasRaw = d.wantedAreas && typeof d.wantedAreas === 'object' ? (d.wantedAreas as Record<string, unknown>) : {};
        const wantedAreas: { [areaKey: string]: string } = {};
        for (const [areaKey, buildingKey] of Object.entries(wantedAreasRaw)) {
            if (typeof buildingKey === 'string' && buildingKey.length > 0) wantedAreas[areaKey] = buildingKey;
        }
        const problems = [
            typeof d.tenantKey !== 'string' ? 'tenantKey' : null,
            typeof d.queueDate !== 'string' || !DATE_ONLY_RE.test(d.queueDate) ? 'queueDate' : null,
            d.status !== 'active' && d.status !== 'closed' ? `status (${String(d.status)})` : null,
            Object.keys(wantedAreas).length === 0 ? 'wantedAreas' : null,
        ].filter(Boolean);
        if (problems.length > 0 || typeof d.tenantKey !== 'string' || typeof d.queueDate !== 'string') {
            // A node from the pre-release v1 schema (buildingKey/buildingKeys, no wantedAreas) lands here; delete it, there is no migration.
            console.warn(`Invalid parking queue entry at ${key}: invalid or missing ${problems.join(', ')}`);
            return null;
        }
        const spotTypes: ParkingQueueEntryData['spotTypes'] = {};
        for (const type of Object.keys((d.spotTypes && typeof d.spotTypes === 'object' ? d.spotTypes : {}) as Record<string, unknown>)) {
            if (SPOT_TYPES.includes(type)) spotTypes[type as ParkingSpotType] = true;
        }
        const log: QueueLogEvent[] = [];
        for (const [eventKey, raw] of Object.entries((d.log && typeof d.log === 'object' ? d.log : {}) as Record<string, unknown>)) {
            const event = this.parseEvent(raw, eventKey);
            if (event) log.push(event);
        }
        return stripUndefined({
            key,
            tenantKey: d.tenantKey,
            queueDate: d.queueDate,
            status: d.status as QueueEntryStatus,
            closeReason: typeof d.closeReason === 'string' && CLOSE_REASONS.includes(d.closeReason) ? (d.closeReason as QueueCloseReason) : undefined,
            closeNote: optionalString(d.closeNote),
            wantedAreas,
            spotTypes: Object.keys(spotTypes).length > 0 ? spotTypes : undefined,
            wantsCharger: optionalWish(d.wantsCharger),
            wantsOutlet: optionalWish(d.wantsOutlet),
            comment: optionalString(d.comment),
            markedInternal: d.markedInternal === true ? (true as const) : undefined,
            log: log.sort((a, b) => (a.when < b.when ? -1 : a.when > b.when ? 1 : byKey(a, b))),
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    parseEntries(data: unknown): ParkingQueueEntry[] {
        if (!data || typeof data !== 'object') return [];
        const entries: ParkingQueueEntry[] = [];
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            const entry = this.parseEntry(value, key);
            if (entry) entries.push(entry);
        }
        return entries;
    }

    // MARK: - Reads

    async getAll(): Promise<ParkingQueueEntry[]> {
        const snapshot = await get(ref(this.db, 'parkingQueue'));
        return snapshot.exists() ? this.parseEntries(snapshot.val()) : [];
    }

    async getByTenant(tenantKey: string): Promise<ParkingQueueEntry[]> {
        const snapshot = await get(query(ref(this.db, 'parkingQueue'), orderByChild('tenantKey'), equalTo(tenantKey)));
        return snapshot.exists() ? this.parseEntries(snapshot.val()) : [];
    }

    async get(entryKey: string): Promise<ParkingQueueEntry | null> {
        const snapshot = await get(ref(this.db, this.getEntryPath(entryKey)));
        return snapshot.exists() ? this.parseEntry(snapshot.val(), entryKey) : null;
    }

    private async requireEntry(entryKey: string): Promise<ParkingQueueEntry> {
        const entry = await this.get(entryKey);
        if (!entry) throw new Error('Köplatsen hittades inte');
        return entry;
    }

    // MARK: - Validation

    private validateInput(input: ParkingQueueEntryInput): ParkingQueueEntryInput {
        const tenantKey = optionalString(input.tenantKey);
        if (!tenantKey) throw new Error('Sökande krävs');
        if (typeof input.queueDate !== 'string' || !DATE_ONLY_RE.test(input.queueDate)) throw new Error('Ogiltigt ködatum');
        const wantedAreas: { [areaKey: string]: string } = {};
        for (const [areaKey, buildingKey] of Object.entries(input.wantedAreas ?? {})) {
            const a = optionalString(areaKey);
            const b = optionalString(buildingKey);
            if (a && b) wantedAreas[a] = b;
        }
        if (Object.keys(wantedAreas).length === 0) throw new Error('Minst ett önskat område krävs');
        const spotTypes = (input.spotTypes ?? []).filter(type => SPOT_TYPES.includes(type));
        if (spotTypes.length !== (input.spotTypes ?? []).length) throw new Error('Ogiltig platstyp');
        return {
            tenantKey,
            queueDate: input.queueDate,
            wantedAreas,
            spotTypes: spotTypes.length > 0 ? [...spotTypes].sort() : undefined,
            wantsCharger: optionalWish(input.wantsCharger),
            wantsOutlet: optionalWish(input.wantsOutlet),
            comment: optionalString(input.comment),
            markedInternal: input.markedInternal === true ? true : undefined,
        };
    }

    /** The tenant, every wanted area and the unit must exist; stale picks get a readable message, not a permission error. */
    private async assertReferencesExist(input: ParkingQueueEntryInput): Promise<void> {
        const tenant = await get(ref(this.db, `tenants/${input.tenantKey}`));
        if (!tenant.exists()) throw new Error('Sökanden finns inte längre i registret');
        if ((tenant.val() as Record<string, unknown>).anonymized === true) throw new Error('Sökanden är borttagen');
        for (const [areaKey, buildingKey] of Object.entries(input.wantedAreas)) {
            const area = await get(ref(this.db, `buildings/${buildingKey}/parking/${areaKey}/name`));
            if (!area.exists()) throw new Error('Ett av de önskade områdena finns inte längre');
        }
    }

    private spotTypesMap(types: ParkingSpotType[] | undefined): ParkingQueueEntryData['spotTypes'] | undefined {
        if (!types || types.length === 0) return undefined;
        const map: ParkingQueueEntryData['spotTypes'] = {};
        for (const type of types) map[type] = true;
        return map;
    }

    // MARK: - Entry mutations

    async create(input: ParkingQueueEntryInput): Promise<ParkingQueueEntry> {
        const uid = await requireAdmin(this.db, 'lägga till köplatser');
        const validated = this.validateInput(input);
        await this.assertReferencesExist(validated);
        const key = this.newKey('parkingQueue', 'köplatsen');
        const eventKey = this.newKey(`${this.getEntryPath(key)}/log`, 'logghändelsen');
        const created = this.event(uid, 'created', { to: validated.queueDate });
        const node: ParkingQueueEntryData = stripUndefined({
            tenantKey: validated.tenantKey,
            queueDate: validated.queueDate,
            status: 'active' as const,
            wantedAreas: validated.wantedAreas,
            spotTypes: this.spotTypesMap(validated.spotTypes),
            wantsCharger: validated.wantsCharger,
            wantsOutlet: validated.wantsOutlet,
            comment: validated.comment,
            markedInternal: validated.markedInternal ? (true as const) : undefined,
            log: { [eventKey]: created },
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });
        await update(ref(this.db), { [this.getEntryPath(key)]: node });
        recordActivity('parkingQueue.created', { targetKey: key });
        return { ...node, key, log: [{ key: eventKey, ...created }] };
    }

    /** Writes field paths and one log event per changed field; the entry key never changes. */
    async update(entryKey: string, input: ParkingQueueEntryInput): Promise<void> {
        const uid = await requireAdmin(this.db, 'ändra köplatser');
        const validated = this.validateInput(input);
        const existing = await this.requireEntry(entryKey);
        if (validated.tenantKey !== existing.tenantKey) throw new Error('Sökanden på en köplats kan inte bytas');
        await this.assertReferencesExist(validated);
        const path = this.getEntryPath(entryKey);
        const updates: Record<string, unknown> = {};
        const logChange = (type: QueueLogEventType, from: string | undefined, to: string | undefined) =>
            Object.assign(updates, this.eventUpdate(entryKey, this.event(uid, type, { from, to })));

        if (validated.queueDate !== existing.queueDate) {
            updates[`${path}/queueDate`] = validated.queueDate;
            logChange('queueDateChanged', existing.queueDate, validated.queueDate);
        }
        const oldAreas = Object.keys(existing.wantedAreas).sort().join(',');
        const newAreas = Object.keys(validated.wantedAreas).sort().join(',');
        if (oldAreas !== newAreas || Object.entries(validated.wantedAreas).some(([a, b]) => existing.wantedAreas[a] !== b)) {
            updates[`${path}/wantedAreas`] = validated.wantedAreas;
            logChange('areasChanged', `${Object.keys(existing.wantedAreas).length} områden`, `${Object.keys(validated.wantedAreas).length} områden`);
        }
        const oldTypes = Object.keys(existing.spotTypes ?? {}).sort().join(',');
        const newTypes = (validated.spotTypes ?? []).join(',');
        if (oldTypes !== newTypes) {
            updates[`${path}/spotTypes`] = this.spotTypesMap(validated.spotTypes) ?? null;
            logChange('spotTypesChanged', oldTypes || 'alla', newTypes || 'alla');
        }
        if (validated.wantsCharger !== existing.wantsCharger) {
            updates[`${path}/wantsCharger`] = validated.wantsCharger ?? null;
            logChange('chargerChanged', existing.wantsCharger ?? 'any', validated.wantsCharger ?? 'any');
        }
        if (validated.wantsOutlet !== existing.wantsOutlet) {
            updates[`${path}/wantsOutlet`] = validated.wantsOutlet ?? null;
            logChange('outletChanged', existing.wantsOutlet ?? 'any', validated.wantsOutlet ?? 'any');
        }
        if (validated.comment !== existing.comment) {
            updates[`${path}/comment`] = validated.comment ?? null;
            logChange('commentChanged', existing.comment, validated.comment);
        }
        if ((validated.markedInternal === true) !== (existing.markedInternal === true)) {
            updates[`${path}/markedInternal`] = validated.markedInternal ? true : null;
            logChange('internalChanged', existing.markedInternal ? 'ja' : 'nej', validated.markedInternal ? 'ja' : 'nej');
        }
        if (Object.keys(updates).length === 0) return;
        Object.assign(updates, { [`${path}/updatedBy`]: uid, [`${path}/updatedWhen`]: formatDateForStorage(new Date()) });
        await update(ref(this.db), updates);
        recordActivity('parkingQueue.updated', { targetKey: entryKey });
    }

    /** Closes the entry by hand with a reason; a lease from the queue closes it through `linkLease`. */
    async close(entryKey: string, reason: QueueCloseReason, note: string | undefined): Promise<void> {
        const uid = await requireAdmin(this.db, 'avsluta köplatser');
        if (!CLOSE_REASONS.includes(reason)) throw new Error('Ogiltig anledning');
        const existing = await this.requireEntry(entryKey);
        if (existing.status === 'closed') throw new Error('Köplatsen är redan avslutad');
        const path = this.getEntryPath(entryKey);
        const closeNote = optionalString(note);
        const updates: Record<string, unknown> = {
            [`${path}/status`]: 'closed',
            [`${path}/closeReason`]: reason,
            [`${path}/closeNote`]: closeNote ?? null,
            ...this.eventUpdate(entryKey, this.event(uid, 'closed', { from: 'active', to: reason, note: closeNote })),
        };
        Object.assign(updates, { [`${path}/updatedBy`]: uid, [`${path}/updatedWhen`]: formatDateForStorage(new Date()) });
        await update(ref(this.db), updates);
        recordActivity('parkingQueue.updated', { targetKey: entryKey });
    }

    async reopen(entryKey: string, note?: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'återöppna köplatser');
        const existing = await this.requireEntry(entryKey);
        if (existing.status !== 'closed') throw new Error('Köplatsen är inte avslutad');
        const path = this.getEntryPath(entryKey);
        await update(ref(this.db), {
            [`${path}/status`]: 'active',
            [`${path}/closeReason`]: null,
            [`${path}/closeNote`]: null,
            ...this.eventUpdate(entryKey, this.event(uid, 'reopened', { from: existing.closeReason, to: 'active', note: optionalString(note) })),
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
        recordActivity('parkingQueue.updated', { targetKey: entryKey });
    }

    async addNote(entryKey: string, note: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'anteckna på köplatser');
        const text = optionalString(note);
        if (!text) throw new Error('Anteckningen är tom');
        await this.requireEntry(entryKey);
        await update(ref(this.db), this.eventUpdate(entryKey, this.event(uid, 'note', { note: text })));
        recordActivity('parkingQueue.updated', { targetKey: entryKey });
    }

    // MARK: - Leases

    private async resolveSpot(target: SpotTarget): Promise<{ ref: SpotRef; label: string }> {
        const areaSnapshot = await get(ref(this.db, `buildings/${target.buildingKey}/parking/${target.areaKey}`));
        const area = areaSnapshot.exists() ? (areaSnapshot.val() as Record<string, unknown>) : undefined;
        const spots = area?.spots as Record<string, Record<string, unknown>> | undefined;
        const spot = spots?.[target.spotKey];
        if (!area || !spot) throw new Error('Parkeringsplatsen hittades inte');
        const type = typeof spot.type === 'string' && SPOT_TYPES.includes(spot.type) ? (spot.type as ParkingSpotType) : ParkingSpotType.Car;
        return {
            ref: {
                ...target,
                spot: stripUndefined({
                    type,
                    chargerComponentKey: optionalString(spot.chargerComponentKey),
                    hasOutlet: spot.hasOutlet === true ? true : undefined,
                }),
            },
            label: parkingSpotLeaseLabel(typeof area.name === 'string' ? area.name : '', typeof spot.number === 'string' ? spot.number : ''),
        };
    }

    /**
     * The entries passed over if `entryKey` gets the spot (FR20). The lease
     * flows call this to show the reason field; `linkLease` recomputes it at
     * save. `exceptLeaseKey` leaves out the lease just written for the spot,
     * so the order is the one from before it (the new lease would otherwise
     * count as a spot the applicant already holds).
     */
    async deviationFor(entryKey: string, target: SpotTarget, exceptLeaseKey?: string): Promise<ParkingQueueEntry[]> {
        const [{ ref: spotRef }, entries, leases, config] = await Promise.all([
            this.resolveSpot(target),
            this.getAll(),
            leasesApi.getAll(),
            fetchParkingQueueConfig(),
        ]);
        const today = todayStockholm();
        const rows = entries.map(entry => rowFacts(entry, leases.filter(l => l.key !== exceptLeaseKey), today));
        return entriesAhead(rows, spotRef, config, entryKey).map(row => row.entry);
    }

    /**
     * Active entries a parking lease written outside the queue may fill: the
     * lease's tenant, wanting the spot's area. Spot type and charger/outlet
     * wishes are not checked (the tenant took this spot anyway); co-tenants
     * are not matched.
     */
    async entriesForLease(tenantKey: string, target: Pick<SpotTarget, 'buildingKey' | 'areaKey'>): Promise<ParkingQueueEntry[]> {
        const entries = await this.getByTenant(tenantKey);
        return entries
            .filter(e => e.status === 'active' && e.wantedAreas[target.areaKey] === target.buildingKey)
            .sort((a, b) => (a.queueDate < b.queueDate ? -1 : a.queueDate > b.queueDate ? 1 : byKey(a, b)));
    }

    /**
     * Links a lease written for the entry and closes the entry as accepted, in
     * one write: `deviation` (when someone stood ahead, FR20, refused without
     * a reason), `leaseLinked` and `closed`. The lease itself is written first
     * through leasesApi; the queue never touches `currentLeaseKey`.
     */
    async linkLease(entryKey: string, input: QueueLeaseLinkInput): Promise<void> {
        const uid = await requireAdmin(this.db, 'koppla hyresavtal till köplatser');
        const leaseKey = optionalString(input.leaseKey);
        if (!leaseKey) throw new Error('Hyresavtal krävs');
        const target: SpotTarget = {
            buildingKey: optionalString(input.buildingKey) ?? '',
            areaKey: optionalString(input.areaKey) ?? '',
            spotKey: optionalString(input.spotKey) ?? '',
        };
        if (!target.buildingKey || !target.areaKey || !target.spotKey) throw new Error('Parkeringsplats krävs');
        const existing = await this.requireEntry(entryKey);
        if (existing.status !== 'active') throw new Error('Köplatsen är redan avslutad');
        const { label } = await this.resolveSpot(target);
        const ahead = await this.deviationFor(entryKey, target, leaseKey);
        const reason = optionalString(input.reason);
        if (ahead.length > 0 && !reason) {
            throw new Error(`Ange en anledning: ${ahead.length} sökande står före i standardordningen`);
        }
        const path = this.getEntryPath(entryKey);
        const updates: Record<string, unknown> = {};
        if (ahead.length > 0) {
            Object.assign(updates, this.eventUpdate(entryKey, this.event(uid, 'deviation', {
                from: label,
                to: String(ahead.length),
                note: reason,
                aheadEntryKeys: Object.fromEntries(ahead.map(e => [e.key, true as const])),
            })));
        }
        Object.assign(updates, {
            ...this.eventUpdate(entryKey, this.event(uid, 'leaseLinked', { from: label, to: leaseKey })),
            ...this.eventUpdate(entryKey, this.event(uid, 'closed', { from: 'active', to: 'accepted' })),
            [`${path}/status`]: 'closed',
            [`${path}/closeReason`]: 'accepted',
            [`${path}/closeNote`]: null,
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
        await update(ref(this.db), updates);
        recordActivity('parkingQueue.updated', { targetKey: entryKey });
    }
}

export const parkingQueueApi = new ParkingQueueApi();
